import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { 
  Video, MessageCircle, Phone, Mail, Clock, ArrowRight,
  Shield, CheckCircle, XCircle, Loader2, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const assistanceTypes = [
  {
    id: 'chat',
    name: 'Chat',
    description: 'Conversa por texto em tempo real com o profissional',
    icon: MessageCircle,
    color: 'bg-[#4A5D4E]',
    startingPrice: 39.90,
    duration: '30 min',
    features: [
      'Resposta em tempo real',
      'Histórico salvo',
      'Envio de arquivos',
      'Ideal para dúvidas rápidas'
    ],
    popular: true
  },
  {
    id: 'video',
    name: 'Vídeo Call',
    description: 'Sessão por videochamada para atendimento completo',
    icon: Video,
    color: 'bg-[#6B8E7C]',
    startingPrice: 79.90,
    duration: '50 min',
    features: [
      'Face a face virtual',
      'Conexão mais próxima',
      'Compartilhamento de tela',
      'Ideal para sessões completas'
    ]
  },
  {
    id: 'call',
    name: 'Ligação',
    description: 'Atendimento por telefone ou áudio',
    icon: Phone,
    color: 'bg-[#D4A373]',
    startingPrice: 59.90,
    duration: '30 min',
    features: [
      'Praticidade total',
      'Sem necessidade de câmera',
      'Atenda de qualquer lugar',
      'Ideal para quem prefere privacidade'
    ]
  },
  {
    id: 'email',
    name: 'Email / Texto',
    description: 'Orientação detalhada por escrito',
    icon: Mail,
    color: 'bg-[#9B8BAE]',
    startingPrice: 49.90,
    duration: 'Resposta em 24h',
    features: [
      'Resposta elaborada',
      'Tempo para reflexão',
      'Pode reler quando quiser',
      'Ideal para questões complexas'
    ]
  }
];

export default function PaymentsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Check if returning from payment
  const sessionId = searchParams.get('session_id');

  React.useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    setCheckingStatus(true);

    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Não foi possível verificar. Verifique seu email.' });
      setCheckingStatus(false);
      return;
    }

    try {
      const { data } = await axios.get(`${API_URL}/api/payments/status/${sid}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      if (data.payment_status === 'paid') {
        setPaymentStatus({ status: 'success', message: 'Pagamento confirmado!' });
        setCheckingStatus(false);
      } else if (data.status === 'expired') {
        setPaymentStatus({ status: 'expired', message: 'Sessão expirada.' });
        setCheckingStatus(false);
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (error) {
      setPaymentStatus({ status: 'error', message: 'Erro ao verificar.' });
      setCheckingStatus(false);
    }
  };

  // Payment result view
  if (sessionId) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 py-20" data-testid="payment-result">
          <div className="max-w-lg mx-auto px-6 text-center">
            {checkingStatus ? (
              <div className="animate-fade-in">
                <Loader2 className="w-16 h-16 mx-auto text-[#4A5D4E] animate-spin mb-6" />
                <h1 className="text-2xl font-light text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Verificando pagamento...
                </h1>
              </div>
            ) : paymentStatus?.status === 'success' ? (
              <div className="animate-fade-in">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#6B8E7C]/10 flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-[#6B8E7C]" strokeWidth={1.5} />
                </div>
                <h1 className="text-3xl font-light text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Pagamento Confirmado!
                </h1>
                <p className="text-[#63635E] mb-8">Sua sessão foi agendada com sucesso.</p>
                <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2">
                  Ver minhas sessões
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="animate-fade-in">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#B85C5C]/10 flex items-center justify-center mb-6">
                  <XCircle className="w-10 h-10 text-[#B85C5C]" strokeWidth={1.5} />
                </div>
                <h1 className="text-3xl font-light text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {paymentStatus?.message || 'Algo deu errado'}
                </h1>
                <Link to="/payments" className="btn-secondary">Tentar novamente</Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />

      <main className="flex-1 py-12" data-testid="payments-page">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F6EBE6] text-[#4A5D4E] text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              Escolha como quer ser atendido
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Tipos de Assistência
            </h1>
            <p className="text-[#63635E] max-w-2xl mx-auto">
              Escolha o formato de atendimento que melhor se adapta às suas necessidades. 
              Todos os profissionais estão prontos para ajudar.
            </p>
          </div>

          {/* Assistance Types */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {assistanceTypes.map((type, idx) => (
              <div 
                key={type.id}
                className={`card-theraflow relative overflow-hidden animate-fade-in ${type.popular ? 'ring-2 ring-[#4A5D4E]' : ''}`}
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`assistance-${type.id}`}
              >
                {type.popular && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-[#4A5D4E] text-white text-xs font-medium rounded-full">
                    Mais Popular
                  </div>
                )}
                
                <div className="p-6">
                  <div className={`w-14 h-14 rounded-2xl ${type.color} flex items-center justify-center mb-5`}>
                    <type.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  
                  <h3 className="text-xl font-medium text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {type.name}
                  </h3>
                  
                  <p className="text-sm text-[#63635E] mb-4 min-h-[40px]">
                    {type.description}
                  </p>

                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-sm text-[#A0A09A]">a partir de</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-light text-[#292927]">R${type.startingPrice.toFixed(2).replace('.', ',')}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-[#63635E] mb-6">
                    <Clock className="w-4 h-4" strokeWidth={1.5} />
                    {type.duration}
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {type.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-[#63635E]">
                        <CheckCircle className="w-4 h-4 text-[#6B8E7C] flex-shrink-0 mt-0.5" strokeWidth={2} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to="/match"
                    className={`w-full py-3 rounded-full font-medium transition-all flex items-center justify-center gap-2 ${
                      type.popular 
                        ? 'bg-[#4A5D4E] text-white hover:bg-[#3E4E41]' 
                        : 'bg-[#F6EBE6] text-[#292927] hover:bg-[#EBDCD5]'
                    }`}
                    data-testid={`select-${type.id}`}
                  >
                    <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                    Encontrar Match
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* How it works */}
          <div className="card-theraflow p-8 mb-16">
            <h2 className="text-2xl font-light text-[#292927] text-center mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Como funciona?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Escolha o tipo', desc: 'Selecione chat, vídeo, ligação ou email' },
                { step: '2', title: 'Encontre o profissional', desc: 'Use o matching IA ou navegue pela lista' },
                { step: '3', title: 'Agende e pague', desc: 'Escolha horário e pague com segurança' },
                { step: '4', title: 'Realize a sessão', desc: 'Conecte-se e receba o atendimento' }
              ].map((item, idx) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#4A5D4E] text-white flex items-center justify-center text-lg font-medium mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-medium text-[#292927] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#63635E]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="ai-matching-card text-center">
            <div className="absolute inset-0 animate-shimmer opacity-30" />
            <div className="relative z-10">
              <Sparkles className="w-10 h-10 mx-auto text-[#4A5D4E] mb-4" strokeWidth={1.5} />
              <h2 className="text-2xl font-light text-[#292927] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Não sabe qual profissional escolher?
              </h2>
              <p className="text-[#63635E] mb-6">
                Deixe nossa IA encontrar o match perfeito para você
              </p>
              <Link to="/match" className="btn-primary inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                Encontrar meu Match
              </Link>
            </div>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 text-center">
            <div className="flex flex-col items-center">
              <Shield className="w-8 h-8 text-[#4A5D4E] mb-3" strokeWidth={1.5} />
              <h4 className="font-medium text-[#292927] mb-1">Pagamento Seguro</h4>
              <p className="text-sm text-[#63635E]">Processado via Stripe com criptografia</p>
            </div>
            <div className="flex flex-col items-center">
              <CheckCircle className="w-8 h-8 text-[#4A5D4E] mb-3" strokeWidth={1.5} />
              <h4 className="font-medium text-[#292927] mb-1">Profissionais Verificados</h4>
              <p className="text-sm text-[#63635E]">Todos passam por verificação da comunidade</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 text-[#4A5D4E] mb-3" strokeWidth={1.5} />
              <h4 className="font-medium text-[#292927] mb-1">Flexibilidade Total</h4>
              <p className="text-sm text-[#63635E]">Agende no horário que preferir</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
